import { IFormStep } from '../../utils/formStep';
import React, { ChangeEvent } from 'react';
import { withTranslation } from 'react-i18next';
import {
    accountFromFormResult,
    handleInputWithLabelOnBlur,
    handleInputWithLabelOnFocus,
    startOverId,
    termsAndConditionsEn,
    termsAndConditionsFr,
} from '../../registration';
import Contact from '../../contact/step1/contact';
import OrganizationStep1OrganizationProfile from '../step1/organizationStep1OrganizationProfile';
import OrganizationStep1BusinessInformations from '../step1/organizationStep1BusinessInformations';
import OrganizationStep1AdditionalInformation from '../step1/organizationStep1AdditionalInformation';
import OrganizationStep1Reasons from '../step1/organizationStep1Reasons';
import OrganizationStep2AdministratorProfile from '../step2/organizationStep2AdministratorProfile';
import OrganizationStep3Provider from '../step3/organizationStep3Provider';
import fetchFormData from '../../../../helpers/fetchFormData';
import { updateAccount } from '../../../../helpers/account';
import { isErrorModel } from '../../../../helpers/models/errorModel';
import { getAbsoluteRouteForCurrentHost, ROUTE_CONFIRMATION } from '../../../../routes/routes';
import { IAccountModel } from '../../../../helpers/models/accountModel';
import { REFERENCE_NO_PARAM } from '../../../../helpers/network';
import { handleGenericFormError, scrollToFormErrors } from '../../utils/formErrors';
import { IProviderModel } from '../../../../helpers/models/providerModel';
import { IProvinceModel } from '../../../../helpers/models/provinceModel';
import { IRoleModel } from '../../../../helpers/models/roleModel';
import { ReactComponent as NotificationLogo } from '../../../../styles/img/icons/error.svg';

const OrganizationReviseSubmit = (props: IFormStep) => {
    const { t, account, i18n } = props;

    const handleSubmit = async (e: any) => {
        e.preventDefault();
        const data = fetchFormData(e);

        const formResult = Object.assign({}, ...data.map((x) => ({ [x.name]: x.value })));
        const authorization = data.filter((element: any) => {
            return element.name === 'form_authorization_communication_submit';
        });
        const errorList = handleGenericFormError(authorization, t);

        // Send data to backend
        if (!errorList.length) {
            const accountDetails = accountFromFormResult(formResult, account);
            accountDetails.registrationComplete = true;

            // update account
            const result = await updateAccount(i18n.language, account.id, { ...accountDetails });
            if (isErrorModel(result)) {
                errorList.push({ message: 'form_generic_error', weight: 0 });
                scrollToFormErrors();
            } else {
                // Lead to result
                window.location.replace(
                    getAbsoluteRouteForCurrentHost(ROUTE_CONFIRMATION) +
                        `?${REFERENCE_NO_PARAM}=${(result as IAccountModel).referenceNo}`
                );
            }
        } else {
            document.getElementById('revise-submit-error')!.style.display = 'flex';
        }

        props.setError(errorList);
    };

    const handleUpdateStep = (step: number) => {
        const newFormData = Object.assign({}, account);
        if (newFormData.step >= 1) {
            newFormData.step = step;
        }

        // set number to province and role instead of the returned object
        newFormData.province = +(account.province as IProvinceModel).id;
        newFormData.role = +(account.role as IRoleModel).id;
        updateAccount(i18n.language, account.id, { ...newFormData });
    };

    const handleCheckboxChange = (e: ChangeEvent<HTMLInputElement>) => {
        if (e.target.value) {
            document
                .getElementById('form_authorization_communication_submit_label')!
                .classList.remove('registration-error');
            document.getElementById('revise-submit-error')!.style.display = 'none';
        }
    };

    return (
        <form className="registration-container" onSubmit={handleSubmit}>
            <div className="registration-form-revise-submit-section">
                <Contact readonly account={account} />
            </div>
            <div className="registration-form-revise-submit-section registration-form-revise-submit-section--white registration-form-revise-submit-section--no-border">
                <OrganizationStep1OrganizationProfile
                    account={account}
                    readonly
                    updateStep={(e: any) => handleUpdateStep(e.target.dataset.step)}
                />
                <OrganizationStep1BusinessInformations account={account} readonly />
                <OrganizationStep1AdditionalInformation account={account} readonly />
                <OrganizationStep1Reasons account={account} readonly />
            </div>
            <div className="registration-form-revise-submit-section registration-form-revise-submit-section--white registration-form-revise-submit-section--no-border">
                <OrganizationStep2AdministratorProfile
                    account={account}
                    readonly
                    updateStep={(e: any) => handleUpdateStep(e.target.dataset.step)}
                />
            </div>
            <div className="registration-form-revise-submit-section registration-form-revise-submit-section--white">
                <div className="registration-form-revise-submit--modify-section">
                    <h1 className="header-3 header-3--purple registration_form_header">
                        {t('form_revise_submit_organization_step_3_providers_title')}
                    </h1>
                    <a
                        data-step={3}
                        className="standalone-link registration_form--modify-button"
                        tabIndex={0}
                        role="button"
                        onClick={(e) => {
                            handleUpdateStep(3);
                        }}
                        aria-label={t('form_revise_submit_organization_step_3_providers_title')
                            .addSpace()
                            .concat(t('form_revise_submit_professional_step_1_modify_btn'))}
                    >
                        {t('form_revise_submit_professional_step_1_modify_btn')}
                        <span className="registration-from--modify-button-icon"></span>
                    </a>
                </div>
                {account.provider.map((item: IProviderModel, index: number) => {
                    return (
                        <OrganizationStep3Provider key={index} index={index} {...props} readonly />
                    );
                })}
            </div>
            <div className="registration-form-revise-submit-section registration-form-revise-submit-section--white">
                <label
                    id="form_authorization_communication_submit_label"
                    htmlFor="form_authorization_communication_submit"
                    className="checkbox form_authorization_eligible_label"
                >
                    <div>
                        {t('form_revise_submit_organization_field_communication')}
                        <a
                            className="standalone-link"
                            href={
                                i18n.language === 'en' ? termsAndConditionsEn : termsAndConditionsFr
                            }
                            target="_blank"
                            rel="noreferrer"
                        >
                            {t('form_revise_submit_organization_field_communication_terms')}
                        </a>
                    </div>
                    <input
                        className="checkbox-input field-value"
                        type="checkbox"
                        id="form_authorization_communication_submit"
                        name="form_authorization_communication_submit"
                        onBlur={handleInputWithLabelOnBlur}
                        onFocus={handleInputWithLabelOnFocus}
                        onChange={handleCheckboxChange}
                        data-set-weight={0}
                    />
                    <span className="checkmark checkmark--disabled"></span>
                </label>
                <div id="revise-submit-error" className="radio-button-error">
                    <NotificationLogo className="radio-button-error-icon" />
                    <span className="field-value radio-button-error-value registration-error">
                        Required
                    </span>
                </div>
                <div className="registration-organization-button-container">
                    <button
                        className="button button--secondary registration-professional-button"
                        onClick={props.startOver}
                        id={startOverId}
                    >
                        {t('form_revise_submit_button_restart')}
                    </button>
                    <div className="registration-form-revise-submit-standalone-button-container">
                        <a
                            data-step={3}
                            tabIndex={0}
                            className="standalone-link registration-button-standalone-link registration-form-revise-submit-standalone-link"
                            onClick={props.goBack}
                            role="button"
                        >
                            {t('form_revise_submit_organization_step_2_button_previous')}
                        </a>
                        <button
                            className="button button--primary registration-form-revise-submit-button"
                            type="submit"
                        >
                            {t('form_revise_submit_button_submit')}
                        </button>
                    </div>
                </div>
            </div>
        </form>
    );
};

export default withTranslation()(OrganizationReviseSubmit);
